
import React, { useEffect, useState, useCallback } from 'react';
import {
  IResourceComponentsProps,
  useTranslate,
  BaseKey,
  useApiUrl,
  useSelect,
} from '@pankod/refine-core';
import { useForm, Tabs, Edit as RefineEdit } from '@pankod/refine-antd';
import { Form, message, type FormInstance, UploadFile, Button } from 'antd';
import dayjs from 'dayjs';
import { Ownership, User } from 'interfaces';
import UserHandler from 'util/UserHandler';
import { OwnershipType } from 'types/ownership/ownershipType';
import { ConveyanceStatus } from 'types/ownership/conveyanceStatus';
import { ConveyanceType } from 'types/ownership/conveyanceType';
import { ConveyanceAttachmentType } from 'types/ownership/conveyanceAttachmentType';
import { AttachmentValidationStatus } from 'types/ownership/attachmentValidationStatus';
import { OwnershipAttachmentType } from 'types/ownership/ownershipAttachmentType';
import { EnumToArray } from 'util/parseData';
import axios, { AxiosResponse } from 'axios';
import { Country, State } from 'country-state-city';
import { CustomTable, CustomTableProps } from 'components/data/customTable';
import { OwnershipForm } from './ownershipFrom';
import { ConveyanceForm } from './conveyanceFrom';
import { CustomUploadFile } from 'types/ownership/customUploadFile';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';

export interface Attachment {
  path?: string;
  conveyance_type?: ConveyanceAttachmentType | null;
  ownership_type?: OwnershipAttachmentType | null;
  custom_type?: string | null;
  details?: string | null;
  document_number?: string | null;
  issue_date?: string | null;
  expiration_date?: string | null;
  validation_status?: AttachmentValidationStatus;
  observations?: string | null;
}

interface Conveyance {
  id: number;
  ownership_id: number;
  grant_to: number;
  status: ConveyanceStatus;
  type: ConveyanceType;
  title: string;
  description: string | null;
  amount: number;
  interval: number;
  auto_renew?: boolean;
  start: string | null;
  end: string | null;
  note: string | null;
  updated_by: number;
  created_by: number;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  attachments: Attachment[];
}

interface ApiResponse<T> {
  data: T;
}

interface tabDataProps {
  conveyances: CustomTableProps;
}

export const OwnershipEdit: React.FC<IResourceComponentsProps> = () => {
  const { form: ownershipForm, onFinish: onOwnershipFinish, queryResult } = useForm<Ownership>({
    resource: 'ownership',
  });
  const [formConveyance] = Form.useForm();
  const apiUrl = useApiUrl();
  const t = useTranslate();
  const authUser = UserHandler.getInstance().user as User;
  const [ownershipType, setOwnershipType] = useState<OwnershipType | undefined>();
  const typeOptions = EnumToArray(OwnershipType).map((op) => {
    op.label = t(`types.${op.label}`, { ns: 'ownerships' });
    return op;
  });
  const conveyanceStatusOptions = EnumToArray(ConveyanceStatus).map((op) => {
    op.label = t(`status.${op.label}`, { ns: 'conveyances' });
    return op;
  });
  const conveyanceTypeOptions = EnumToArray(ConveyanceType).map((op) => {
    op.label = t(`types.${op.label}`, { ns: 'conveyances' });
    return op;
  });
  const attachmentTypeOptions = EnumToArray(ConveyanceAttachmentType).map((op) => {
    op.label = t(`types.${op.label}`, { ns: 'attachments' });
    return op;
  });
  const attachmentStatusOptions = EnumToArray(AttachmentValidationStatus).map((op) => {
    op.label = t(`status.${op.label}`, { ns: 'attachments' });
    return op;
  });
  const [usersForOwnership, setUsersForOwnership] = useState<{ value: BaseKey; label: string }[]>([]);
  const [usersForConveyance, setUsersForConveyance] = useState<
    { id: any; name: string; phone: string; email: string }[]
  >([]);
  const [country, setCountry] = useState<{ value: BaseKey; label: string }[]>([]);
  const [states, setStates] = useState<{ value: BaseKey; label: string }[]>([]);
  const [countrySel, setCountrySel] = useState<string>('');
  const [stateSel, setStateSel] = useState<string>('');
  const [newUserVisible, setNewUserVisible] = useState(false);
  const [newConveyanceVisible, setNewConveyanceVisible] = useState(false);
  const [conveyanceStatus, setConveyanceStatus] = useState<ConveyanceStatus | undefined>();
  const [newUserPhone, setNewUserPhone] = useState<string>();
  const [newUserEmail, setNewUserEmail] = useState<string>();
  const [newUserError, setNewUserError] = useState(false);
  const [newConveyanceError, setNewConveyanceError] = useState(false);
  const { options: userOptions, queryResult: queryResultUser } = useSelect<User>({
    resource: 'user',
    optionLabel: ((user: User) =>
      `${user.lastname} ${user.firstname}`.trim() || user.phone) as any,
    optionValue: 'id',
  });
  const [fileList, setFileList] = useState<CustomUploadFile[]>([]);
  const [conveyanceAttachments, setConveyanceAttachments] = useState<CustomUploadFile[]>([]);
  const [tabItems, setTabItems] = useState<tabDataProps>({
    conveyances: {
      columns: [],
      dataSource: [],
      config: { key: 'id', edit: null, delete: null },
    },
  });

  const initializeFileList = useCallback(
    (attachments: Attachment[] | undefined) => {
      const files: CustomUploadFile[] = attachments?.length
        ? attachments
            .filter((a) => a.path)
            .map((a, index) => ({
              uid: `file-${index}-${Date.now()}`,
              name: a.path!.split('/').pop() || 'document',
              status: 'done' as const,
              url: process.env.REACT_APP_SERVER + a.path!,
              path: a.path!,
              ownership_type: a.ownership_type || OwnershipAttachmentType.Image,
              custom_type: a.custom_type || null,
              details: a.details || '',
              document_number: a.document_number || '',
              issue_date: a.issue_date || null,
              expiration_date: a.expiration_date || null,
              validation_status: a.validation_status || AttachmentValidationStatus.waiting,
              observations: a.observations || '',
            }))
        : [];
      setFileList(files);
      ownershipForm.setFieldsValue({
        attachments: attachments?.map((a) => ({
          ...a,
          issue_date: a.issue_date ? dayjs(a.issue_date) : null,
          expiration_date: a.expiration_date ? dayjs(a.expiration_date) : null,
        })),
      });
    },
    [ownershipForm],
  );

  const onSearchUser = (searchText: string) => {
    const filteredUsersForOwnership: { value: BaseKey; label: string }[] = [];
    const filteredUsersForConveyance: { id: any; name: string; phone: string; email: string }[] = [];
    const userData = queryResultUser?.data?.data as User[] | undefined;
    userData
      ?.filter((user) => {
        const fullName = `${user.lastname} ${user.firstname}`.trim();
        return fullName !== '- -'
          ? fullName.toLowerCase().includes(searchText.toLowerCase())
          : user.phone.toLowerCase().includes(searchText.toLowerCase());
      })
      .forEach((user) => {
        if (user.id) {
          const value = user.id;
          const label = `${user.lastname} ${user.firstname}`.trim() || user.phone;
          filteredUsersForOwnership.push({ value, label });
          filteredUsersForConveyance.push({
            id: user.id,
            name: `${user.lastname} ${user.firstname}`.trim() || user.phone,
            phone: user.phone || 'N/A',
            email: user.email || 'N/A',
          });
        }
      });
    setUsersForOwnership(filteredUsersForOwnership);
    setUsersForConveyance(filteredUsersForConveyance);
  };

  const onSearchCountry = (searchText: string) => {
    const res: { value: BaseKey; label: string }[] = [];
    Country.getAllCountries()
      ?.filter((c: any) => c.name.toLowerCase().includes(searchText.toLowerCase()))
      .forEach((c: any) => {
        if (c.isoCode) {
          const value: BaseKey = c.isoCode;
          const label: string = c.name;
          res.push({ value, label });
        }
      });
    setCountry(res);
  };

  const onSearchState = (searchText: string) => {
    const res: { value: BaseKey; label: string }[] = [];
    State.getStatesOfCountry(countrySel)
      ?.filter((c: any) => c.name.toLowerCase().includes(searchText.toLowerCase()))
      .forEach((c: any) => {
        if (c.isoCode) {
          const value: BaseKey = c.isoCode;
          const label: string = c.name;
          res.push({ value, label });
        }
      });
    setStates(res);
  };

  const modalConveyance = (data: Conveyance | null) => {
    if (data) {
      console.log('Informations de la conveyance:', JSON.stringify(data, null, 2));
      const attachments = Array.isArray(data.attachments) ? data.attachments : [];
      formConveyance.setFieldsValue({
        ...data,
        start: data.start ? dayjs(data.start) : null,
        end: data.end ? dayjs(data.end) : null,
        attachments: attachments.map((a) => ({
          ...a,
          issue_date: a.issue_date ? dayjs(a.issue_date) : null,
          expiration_date: a.expiration_date ? dayjs(a.expiration_date) : null,
          conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
        })),
      });
      setConveyanceStatus(data.status);
      setConveyanceAttachments(
        attachments.map((a, index) => ({
          uid: `attach-${index}-${Date.now()}`,
          name: a.path?.split('/').pop() || 'document',
          status: 'done' as const,
          url: `${process.env.REACT_APP_SERVER}${a.path || ''}`,
          path: a.path || '',
          conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
          custom_type: a.custom_type || null,
          details: a.details || '',
          document_number: a.document_number || '',
          issue_date: a.issue_date || null,
          expiration_date: a.expiration_date || null,
          validation_status: a.validation_status || AttachmentValidationStatus.waiting,
          observations: a.observations || '',
        })),
      );
    } else {
      formConveyance.resetFields();
      setConveyanceAttachments([]);
      setConveyanceStatus(undefined);
    }
    setNewConveyanceVisible(true);
  };

  const newConveyanceHandleOk = async () => {
    setNewConveyanceError(false);
    try {
      await formConveyance.validateFields();
      const formData = { ...formConveyance.getFieldsValue() };
      formData.ownership_id = queryResult?.data?.data?.id;
      formData.created_by = authUser.id;
      formData.updated_by = authUser.id;
      formData.start = formData.start ? formData.start.toISOString() : null;
      formData.end = formData.end ? formData.end.toISOString() : null;
      formData.amount = formData.amount ? parseFloat(formData.amount) : 0;
      formData.interval = formData.interval ? parseInt(formData.interval, 10) : 0;
      formData.attachments = (formData.attachments || []).map((a: any) => ({
        ...a,
        path: a.path || a.response?.filePath || null,
        issue_date: a.issue_date
          ? dayjs.isDayjs(a.issue_date)
            ? a.issue_date.toISOString()
            : typeof a.issue_date === 'string'
            ? new Date(a.issue_date).toISOString()
            : null
          : null,
        expiration_date: a.expiration_date
          ? dayjs.isDayjs(a.expiration_date)
            ? a.expiration_date.toISOString()
            : typeof a.expiration_date === 'string'
            ? new Date(a.expiration_date).toISOString()
            : null
          : null,
        conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
        custom_type: a.custom_type || null,
        validation_status: a.validation_status || AttachmentValidationStatus.waiting,
        observations: a.observations || null,
        document_number: a.document_number || null,
        details: a.details || null,
      }));

      let response: AxiosResponse<ApiResponse<Conveyance>>;
      if (formData.id) {
        response = await axios.patch(`${apiUrl}/conveyance/${formData.id}`, formData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${UserHandler.getInstance().token}`,
          },
        });
      } else {
        response = await axios.post(`${apiUrl}/conveyance`, formData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${UserHandler.getInstance().token}`,
          },
        });
      }

      if (response.status === 200 || response.status === 201) {
        message.success(t('messages.successConveyanceSaved', { ns: 'conveyances', defaultValue: 'Conveyance enregistrée avec succès !' }));
        setNewConveyanceVisible(false);
        formConveyance.resetFields();
        setConveyanceAttachments([]);

        if (formData.id && response.data.data?.attachments) {
          const updatedConveyance = response.data.data;
          const newDataSource = tabItems.conveyances.dataSource.map((item: any) =>
            item.id === formData.id
              ? {
                  ...updatedConveyance,
                  typeLb: t(`types.${updatedConveyance.type}`, { ns: 'conveyances' }) || updatedConveyance.type || 'Unknown',
                  statusLb: t(`status.${updatedConveyance.status}`, { ns: 'conveyances' }) || updatedConveyance.status || 'Unknown',
                  amount: updatedConveyance.amount ? parseFloat(updatedConveyance.amount.toString()) : 0,
                  interval: updatedConveyance.interval ? parseInt(updatedConveyance.interval.toString(), 10) : 0,
                  attachments: Array.isArray(updatedConveyance.attachments)
                    ? updatedConveyance.attachments.map((a: Attachment) => ({
                        ...a,
                        path: a.path || null,
                        conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
                        validation_status: a.validation_status || AttachmentValidationStatus.waiting,
                      }))
                    : [],
                }
              : item,
          );

          setTabItems((prevState) => ({
            ...prevState,
            conveyances: {
              ...prevState.conveyances,
              dataSource: newDataSource,
            },
          }));
        } else {
          const res: AxiosResponse<ApiResponse<Conveyance[]>> = await axios.get(
            `${apiUrl}/conveyance/ownership/${queryResult?.data?.data?.id}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${UserHandler.getInstance().token}`,
              },
            },
          );

          const newDataSource = res.data.data.map((d: Conveyance) => {
            const typeLb = t(`types.${d.type}`, { ns: 'conveyances' }) || d.type || 'Unknown';
            const statusLb = t(`status.${d.status}`, { ns: 'conveyances' }) || d.status || 'Unknown';
            return {
              ...d,
              typeLb,
              statusLb,
              amount: d.amount ? parseFloat(d.amount.toString()) : 0,
              interval: d.interval ? parseInt(d.interval.toString(), 10) : 0,
              attachments: Array.isArray(d.attachments)
                ? d.attachments.map((a) => ({
                    ...a,
                    path: a.path || null,
                    conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
                    validation_status: a.validation_status || AttachmentValidationStatus.waiting,
                  }))
                : [],
            };
          });

          setTabItems((prevState) => ({
            ...prevState,
            conveyances: {
              ...prevState.conveyances,
              dataSource: newDataSource,
            },
          }));
        }
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setNewConveyanceError(true);
      message.error(t('messages.errorConveyanceSave', { ns: 'conveyances', defaultValue: 'Erreur lors de l’enregistrement de la conveyance.' }));
    }
  };

  const handleRemove = async (file: UploadFile | CustomUploadFile, index?: number): Promise<void> => {
    try {
      const customFile: CustomUploadFile = {
        ...file,
        path: file.response?.filePath || (file as CustomUploadFile).path || '',
        conveyance_type: (file as CustomUploadFile).conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
      };

      const conveyanceId = formConveyance.getFieldValue('id');
      const ownershipId = queryResult?.data?.data?.id;

      if (!customFile.path || !conveyanceId || !ownershipId) {
        if (index !== undefined) {
          setConveyanceAttachments((prev: CustomUploadFile[]) => {
            const newAttachments = [...prev];
            newAttachments.splice(index, 1);
            return newAttachments;
          });
          const currentAttachments = formConveyance.getFieldValue('attachments') || [];
          currentAttachments.splice(index, 1);
          formConveyance.setFieldsValue({ attachments: currentAttachments });
        }
        return;
      }

      const response = await axios.delete(`${apiUrl}/attachments/by-path`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
        },
        data: {
          path: customFile.path,
          ownership_id: ownershipId,
          conveyance_id: conveyanceId,
        },
      });

      if (response.status === 200) {
        if (index !== undefined) {
          setConveyanceAttachments((prev: CustomUploadFile[]) => {
            const newAttachments = [...prev];
            newAttachments.splice(index, 1);
            return newAttachments;
          });
          const currentAttachments = formConveyance.getFieldValue('attachments') || [];
          currentAttachments.splice(index, 1);
          formConveyance.setFieldsValue({ attachments: currentAttachments });
        }
        message.success(t('messages.successFileRemoved', { ns: 'conveyances', defaultValue: 'Fichier supprimé avec succès !' }));
      } else {
        message.error(t('messages.errorFileRemove', { ns: 'conveyances', defaultValue: 'Échec de la suppression du fichier.' }));
      }
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      message.error(t('messages.errorFileRemove', { ns: 'conveyances', defaultValue: 'Erreur lors de la suppression du fichier.' }));
      throw err;
    }
  };

  const handleFinish = async () => {
    try {
      await ownershipForm.validateFields();
      const formData: any = ownershipForm.getFieldsValue();
      formData.attachments = formData.attachments?.map((a: any) => ({
        ...a,
        issue_date: a.issue_date
          ? dayjs.isDayjs(a.issue_date)
            ? a.issue_date.toISOString()
            : typeof a.issue_date === 'string'
            ? a.issue_date
            : null
          : null,
        expiration_date: a.expiration_date
          ? dayjs.isDayjs(a.expiration_date)
            ? a.expiration_date.toISOString()
            : typeof a.expiration_date === 'string'
            ? a.expiration_date
            : null
          : null,
        ownership_type: a.ownership_type || OwnershipAttachmentType.Image,
        custom_type: a.custom_type || null,
      }));
      const response = await axios.get(
        `${process.env.REACT_APP_GEOCODE_API}?address=${formData.address}, ${formData.city} (${formData.province}) ${formData.state}&key=${process.env.REACT_APP_GEOCODE_API_KEY}`,
      );
      if (response?.data?.results) {
        formData.latitude = response?.data?.results[0]?.geometry?.location?.lat;
        formData.longitude = response?.data?.results[0]?.geometry?.location?.lng;
      }
      ownershipForm.setFieldsValue(formData);
      await onOwnershipFinish(formData);
      message.success(t('messages.successFormSaved', { ns: 'ownerships', defaultValue: 'Formulaire enregistré avec succès !' }));
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      message.error(t('messages.errorFormSave', { ns: 'ownerships', defaultValue: 'Erreur lors de la soumission du formulaire.' }));
    }
  };

  useEffect(() => {
    setNewUserError(false);
    onSearchCountry('');
    const ownershipData = queryResult?.data?.data;
    if (ownershipData) {
      setOwnershipType(ownershipData.type);
      setCountrySel(ownershipData.state || '');
      setStateSel(ownershipData.province || '');
      initializeFileList(ownershipData.attachments);
      ownershipForm.setFieldsValue({ ...ownershipData });
    }

    if (ownershipData?.id) {
      axios
        .get(`${apiUrl}/conveyance/ownership/${ownershipData.id}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${UserHandler.getInstance().token}`,
          },
        })
        .then((res) => {
          const cols = [
            {
              title: t('fields.title', { ns: 'conveyances', defaultValue: 'Titre' }),
              name: 'title',
              type: 'text',
              required: false,
              table: 'conveyances',
              editable: false,
            },
            {
              title: t('fields.status', { ns: 'conveyances', defaultValue: 'Statut' }),
              name: 'statusLb',
              type: 'text',
              required: true,
              table: 'conveyances',
              editable: false,
            },
            {
              title: t('fields.type', { ns: 'conveyances', defaultValue: 'Type' }),
              name: 'typeLb',
              type: 'text',
              required: true,
              table: 'conveyances',
              editable: false,
            },
            {
              title: t('fields.amount', { ns: 'conveyances', defaultValue: 'Montant' }),
              name: 'amount',
              type: 'number',
              required: true,
              table: 'conveyances',
              editable: false,
            },
            {
              title: t('fields.start', { ns: 'conveyances', defaultValue: 'Début' }),
              name: 'start',
              type: 'date',
              table: 'conveyances',
              editable: false,
            },
            {
              title: t('fields.end', { ns: 'conveyances', defaultValue: 'Fin' }),
              name: 'end',
              type: 'date',
              table: 'conveyances',
              editable: false,
            },
            {
              title: t('fields.attachments', { ns: 'conveyances', defaultValue: 'Pièces jointes' }),
              name: 'attachments',
              type: 'text',
              editable: false,
              render: (value: Attachment[]) => {
                return value?.length > 0
                  ? value
                      .map((a) => {
                        const fileName = a.path?.split('/').pop() || 'Fichier sans nom';
                        const attachmentType =
                          a.conveyance_type || a.ownership_type || a.custom_type || 'Type inconnu';
                        return `${fileName} (${attachmentType})`;
                      })
                      .join(', ')
                  : t('messages.noAttachments', { ns: 'conveyances', defaultValue: 'Aucun attachment' });
              },
            },
          ];

          setTabItems((prevState) => ({
            ...prevState,
            conveyances: {
              columns: cols,
              dataSource: res.data?.data
                ? res.data.data.map((d: Conveyance) => {
                    const typeLb = t(`types.${d.type}`, { ns: 'conveyances' }) || d.type || 'Unknown';
                    const statusLb = t(`status.${d.status}`, { ns: 'conveyances' }) || d.status || 'Unknown';
                    return {
                      ...d,
                      typeLb,
                      statusLb,
                      amount: d.amount ? parseFloat(d.amount.toString()) : 0,
                      interval: d.interval ? parseInt(d.interval.toString(), 10) : 0,
                      attachments: Array.isArray(d.attachments)
                        ? d.attachments.map((a) => ({
                            ...a,
                            path: a.path || null,
                            conveyance_type: a.conveyance_type || ConveyanceAttachmentType.Acte_de_vente,
                            validation_status: a.validation_status || AttachmentValidationStatus.waiting,
                          }))
                        : [],
                    };
                  })
                : [],
              config: {
                key: 'id',
                edit: modalConveyance,
                delete: null,
                addNew: () => modalConveyance(null),
              },
            },
          }));
        })
        .catch((err) => {
          console.error('Erreur lors de la récupération des conveyances:', err);
          message.error(t('messages.errorLoadingConveyances', { ns: 'conveyances', defaultValue: 'Erreur lors du chargement des conveyances.' }));
        });
    }
  }, [queryResult?.data?.data, initializeFileList, apiUrl, t, ownershipForm]);

  useEffect(() => {
    onSearchUser('');
  }, [queryResultUser?.data?.data]);

  useEffect(() => {
    onSearchState('');
  }, [countrySel]);

  const handleResetFilters = () => {
    formConveyance.resetFields();
    setConveyanceAttachments([]);
    setConveyanceStatus(undefined);
    message.success(t('messages.resetFilters', { ns: 'conveyances', defaultValue: 'Filtres réinitialisés' }));
  };

  const { TabPane } = Tabs;

  return (
    <>
      <style>{`
        .ownership-edit-container {
          padding: 24px;
          background: linear-gradient(135deg, #e6f0ff 0%, #ffffff 100%);
          min-height: 100vh;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }
        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .create-button-container .ant-btn {
          display: flex;
          align-items: center;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
          border: none;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }
        .create-button-container .ant-btn:hover {
          background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        .create-button-container .anticon {
          margin-right: 8px;
          font-size: 16px;
        }
        .reset-button .ant-btn {
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          border-radius: 8px;
          background: #fff;
          border-color: #ff4d4f;
          color: #ff4d4f;
          transition: all 0.3s ease;
        }
        .reset-button .ant-btn:hover {
          background: #fff1f0;
          color: #cf1322;
          border-color: #cf1322;
          transform: translateY(-1px);
        }
        .ant-tabs-nav {
          background: #ffffff;
          border-radius: 8px;
          padding: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }
        .ant-tabs-tab {
          font-weight: 600;
          color: #333;
          padding: 12px 24px;
          transition: color 0.3s ease;
        }
        .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #1890ff !important;
        }
        .ant-tabs-ink-bar {
          background: #1890ff;
          height: 3px;
        }
        .ownership-form-container, .conveyance-form-container {
          background: #ffffff;
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
          margin-bottom: 24px;
        }
        .ant-form-item-label > label {
          font-weight: 600;
          color: #333;
        }
        .ant-form-item {
          margin-bottom: 16px;
        }
        .custom-table-container {
          background: #ffffff;
          border-radius: 8px;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
          overflow-x: auto;
        }
        .custom-table-container .ant-table {
          min-width: 100%;
          border: none;
        }
        .custom-table-container .ant-table-thead > tr > th {
          background: #1890ff;
          color: #ffffff;
          font-weight: 600;
          border-bottom: none;
          padding: 12px 16px;
        }
        .custom-table-container .ant-table-tbody > tr:hover > td {
          background: #f5f7fa;
        }
        .custom-table-container .ant-table-tbody > tr > td {
          padding: 12px 16px;
          color: #333;
          font-weight: 500;
        }
        .action-buttons .ant-btn {
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: all 0.3s ease;
          color: #555;
          border: none;
          background: transparent;
        }
        .action-buttons .ant-btn:hover {
          background: #e6f7ff;
          color: #1890ff;
        }
        .ant-btn-primary {
          background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%) !important;
          border: none !important;
          color: #fff !important;
          font-weight: 600;
          border-radius: 8px;
          padding: 8px 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }
        .ant-btn-primary:hover {
          background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%) !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        @media (max-width: 768px) {
          .ownership-edit-container {
            padding: 16px;
          }
          .custom-table-container .ant-table {
            font-size: 14px;
          }
          .action-buttons .ant-btn {
            width: 28px;
            height: 28px;
          }
          .ant-tabs-tab {
            padding: 8px 16px;
            font-size: 14px;
          }
          .ant-form-item {
            margin-bottom: 12px;
          }
          .create-button-container .ant-btn, .reset-button .ant-btn {
            padding: 6px 12px;
            font-size: 13px;
          }
        }
        @media (max-width: 480px) {
          .ownership-edit-container {
            padding: 12px;
          }
          .custom-table-container .ant-table {
            font-size: 13px;
          }
          .action-buttons .ant-btn {
            width: 24px;
            height: 24px;
          }
          .ant-tabs-tab {
            padding: 6px 12px;
            font-size: 13px;
          }
          .header-container {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>
      <div className="ownership-edit-container">
        <div className="header-container">
          
          
        </div>
        <RefineEdit
          saveButtonProps={{
            onClick: () => handleFinish(),
            className: 'ant-btn-primary',
          }}
        >
          <Tabs defaultActiveKey="1" key="tabs">
            <TabPane tab={t('ownership_data', { ns: 'ownerships', defaultValue: 'Données de propriété' })} key="1">
              <div className="ownership-form-container">
                <OwnershipForm
                  form={ownershipForm as FormInstance<Ownership>}
                  ownershipType={ownershipType}
                  setOwnershipType={setOwnershipType}
                  fileList={fileList}
                  setFileList={setFileList}
                  country={country}
                  setCountry={setCountry}
                  states={states}
                  setStates={setStates}
                  countrySel={countrySel}
                  setCountrySel={setCountrySel}
                  stateSel={stateSel}
                  setStateSel={setStateSel}
                  users={usersForOwnership}
                  selectPropsUser={userOptions}
                  onSearchUser={onSearchUser}
                  onSearchCountry={onSearchCountry}
                  onSearchState={onSearchState}
                  newUserVisible={newUserVisible}
                  setNewUserVisible={setNewUserVisible}
                  newUserError={newUserError}
                  setNewUserError={setNewUserError}
                  newUserPhone={newUserPhone}
                  setNewUserPhone={setNewUserPhone}
                  newUserEmail={newUserEmail}
                  setNewUserEmail={setNewUserEmail}
                  queryResultUser={queryResultUser}
                  typeOptions={typeOptions}
                  authUser={authUser}
                  tabItems={tabItems}
                  apiUrl={apiUrl}
                  onFinish={handleFinish}
                />
              </div>
            </TabPane>
            <TabPane tab={t('conveyances', { ns: 'conveyances', defaultValue: 'Conveyances' })} key="2">
              <div className="custom-table-container">
                <CustomTable {...tabItems.conveyances} key={JSON.stringify(tabItems.conveyances.dataSource)} />
              </div>
            </TabPane>
          </Tabs>
          <ConveyanceForm
            formConveyance={formConveyance}
            newConveyanceVisible={newConveyanceVisible}
            setNewConveyanceVisible={setNewConveyanceVisible}
            newConveyanceError={newConveyanceError}
            setNewConveyanceError={setNewConveyanceError}
            conveyanceStatus={conveyanceStatus}
            setConveyanceStatus={setConveyanceStatus}
            conveyanceAttachments={conveyanceAttachments}
            setConveyanceAttachments={setConveyanceAttachments}
            conveyanceStatusOptions={conveyanceStatusOptions}
            conveyanceTypeOptions={conveyanceTypeOptions}
            attachmentTypeOptions={attachmentTypeOptions}
            attachmentStatusOptions={attachmentStatusOptions}
            selectPropsUser={userOptions}
            users={usersForConveyance}
            onSearchUser={onSearchUser}
            apiUrl={apiUrl}
            queryResult={queryResult}
            authUser={authUser}
            newConveyanceHandleOk={newConveyanceHandleOk}
            handleRemove={handleRemove}
          />
        </RefineEdit>
      </div>
    </>
  );
};
